import { useAppDispatch, useAppSelector } from '@/config/hooks';
import { getFloors } from '@/services/floor.service';
import { getZones } from '@/services/zone.service';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Drawer,
  Form,
  IconButton,
  List,
  SelectPicker,
  Stack,
  TagPicker,
  Message,
  useToaster
} from 'rsuite';
import PlusIcon from '@rsuite/icons/Plus';
import MinusIcon from '@rsuite/icons/Minus';
import { RiUserLocationLine } from 'react-icons/ri';
import { updateAccessProfile } from '@/services/accessProfile.service';

const slimText = {
  fontSize: '0.9em',
  color: '#97969B',
  fontWeight: 'lighter',
  paddingBottom: 5
};

const DrawerUpdate = ({ open, onClose, data }) => {
  const dispatch = useAppDispatch();
  const toaster = useToaster();

  const [formValue, setFormValue] = useState({
    name: data.name,
    description: data.description
  });
  
  const [localAccessList, setLocalAccessList] = useState([]);
  const [selectedZones, setSelectedZones] = useState([]);

  const floors = useAppSelector((state) => state.floor?.data || [])?.map(item => ({
    value: item._id,
    label: item.name
  }));

  const zones = useAppSelector((state) => state.zone?.data || [])?.map(item => ({
    value: item._id,
    label: item.name,
    floor: item.floor
  }));

  useEffect(() => {
    if (data?.zones?.length) {
      const zonesByFloor = data.zones.reduce((acc, zone) => {
        const zoneData = zones.find(z => z.value === zone._id || z.value === zone);
        if (zoneData) {
          if (!acc[zoneData.floor]) {
            acc[zoneData.floor] = [];
          }
          acc[zoneData.floor].push(zone._id || zone);
        }
        return acc;
      }, {});
  
      const accessList = Object.keys(zonesByFloor).map(floorId => ({
        floor: floorId,
        zones: zonesByFloor[floorId]
      }));
  
      if (JSON.stringify(localAccessList) !== JSON.stringify(accessList)) {
        setLocalAccessList(accessList);
      }
  
      const newSelectedZones = data.zones.map(zone => zone._id || zone);
      if (JSON.stringify(selectedZones) !== JSON.stringify(newSelectedZones)) {
        setSelectedZones(newSelectedZones);
      }
    }
  }, [data]);
  
  

  useEffect(() => {
    dispatch(getFloors());
    dispatch(getZones());
  }, [dispatch]);

  const handleAddRow = () => {
    setLocalAccessList([
      ...localAccessList,
      {
        floor: null,
        zones: []
      }
    ]);
  };

  const handleRemoveRow = (index) => {
    const values = [...localAccessList];
    const floorId = values[index]?.floor;
    const floorZones = zones.filter(zone => zone.floor === floorId);
    const newZones = selectedZones.filter(
      zoneId => !floorZones.some(z => z.value === zoneId)
    );
    
    setSelectedZones(newZones);
    values.splice(index, 1);
    setLocalAccessList(values);
  };

  const handleChangeFloor = (index, floorId) => {
    const newArr = [...localAccessList];
    newArr[index].floor = floorId;
    newArr[index].zones = [];
    setLocalAccessList(newArr);
  };

  const handleSubmit = async () => {
    try {
      const updateData = {
        name: formValue.name,
        description: formValue.description,
        zones: selectedZones
      };

      await dispatch(updateAccessProfile(data._id, updateData));
      
      toaster.push(
        <Message type="success" duration={5000} showIcon>
          {t('Access profile updated successfully')}
        </Message>,
        { placement: 'topCenter' }
      );
      
      onClose();
    } catch (error) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(error.message) || t('Error updating access profile')}
        </Message>,
        { placement: 'topCenter' }
      );
    }
  };
  const { t } = useTranslation();

  return (
    <Drawer backdrop="static" size="md" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Edit access profile')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} appearance="primary">
            {t('Save')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          <Form.Group>
            <Form.ControlLabel>{t('Name')}</Form.ControlLabel>
            <Form.Control name="name" placeholder="Enter access profile name" />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Description')}</Form.ControlLabel>
            <Form.Control name="description" placeholder="Enter access profile description" />
          </Form.Group>
          
          <Stack style={{ marginTop: 50, marginBottom: 10, marginLeft: 10 }} spacing={5}>
            <RiUserLocationLine /> <h6 className="title">{t('Manage accesses')} </h6>
          </Stack>
          
          <div className="ml-10">
            <List hover>
              {localAccessList.map((item, index) => (
                <List.Item key={index} index={index + 1}>
                  <Stack justifyContent="space-around" wrap>
                    <div style={{ textAlign: 'left' }}>
                      <div style={slimText}>{t('Floor')}</div>
                      <SelectPicker
                        value={item.floor}
                        onChange={(value) => handleChangeFloor(index, value)}
                        data={floors}
                        placement="auto"
                        style={{ width: 250 }}
                        disabledItemValues={localAccessList
                          .filter((_, i) => i !== index)
                          .map(item => item.floor)
                        }
                      />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={slimText}>Zones</div>
                      <TagPicker
                        data={zones.filter(zone => zone.floor === item.floor)}
                        value={selectedZones}
                        onChange={setSelectedZones}
                        style={{ width: 250 }}
                        placement="auto"
                      />
                    </div>
                    <IconButton
                      onClick={() => handleRemoveRow(index)}
                      icon={<MinusIcon />}
                      size="xs"
                    />
                  </Stack>
                </List.Item>
              ))}
            </List>
            <IconButton
              onClick={handleAddRow}
              icon={<PlusIcon color="var(--rs-primary-800)" />}
              style={{ marginTop: 10 }}
            />
          </div>
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default DrawerUpdate;